import { PlanCard } from '@aibees/shared/features/plan-card';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { ChangeDuration } from '../change-duration';
import { CollapsibleSection } from '../collapsible-section';
import { PlanCellValue } from '../plan-cell-value';
import {
  FREE_PLAN_CONFIG,
  USAGE_BASED_PLAN_CONFIG,
} from '../pricing-cards/config';
import * as SX from './sx';
import type { CollapsedTableProps as Props } from './types';
import { useCollapsedTableData } from './use-collapsed-table-data';

export const CollapsedTable = memo(({ className, ariaLabel }: Props) => {
  const { products, topRows, sections, plansByProduct, yearly } =
    useCollapsedTableData();
  return (
    <TableContainer className={classnames('CollapsedTable', className)}>
      <Table aria-label={ariaLabel} sx={SX.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={SX.headerFeatureCell}>
              <Stack sx={SX.comparePlansCell} gap={11}>
                <Stack gap={1.5} mt={1}>
                  <Typography variant="h5" fontWeight={700} color="grey.800">
                    Compare Plans
                  </Typography>
                  <ChangeDuration />
                </Stack>
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <Divider sx={SX.divider} flexItem />
                  <Box>
                    <Typography
                      variant="body5"
                      color="grey.500"
                      component="span"
                    >
                      Need a Custom Solution?{' '}
                    </Typography>
                    <Link
                      href="#"
                      underline="always"
                      variant="body5"
                      fontWeight={600}
                      color="primary.main"
                    >
                      Book a Demo
                    </Link>
                  </Box>
                </Stack>
              </Stack>
            </TableCell>
            {products.map((product) => {
              const name = product.name ?? '';
              const key = name.toLowerCase();
              const isFree = key === 'free';
              const isMostPopular = key === 'builder';
              const plans = plansByProduct[product.id] ?? [];
              const config = isFree
                ? FREE_PLAN_CONFIG
                : USAGE_BASED_PLAN_CONFIG;

              return (
                <TableCell
                  key={product.id}
                  align="center"
                  sx={SX.headerPlanCell}
                >
                  <PlanCard
                    plans={plans}
                    yearly={yearly}
                    isFree={isFree}
                    config={config}
                    variant="compact"
                    title={`${name} Plan`}
                    isMostPopular={isMostPopular}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {topRows.map((row) => (
            <TableRow key={row.feature} sx={SX.featureRow}>
              <TableCell sx={SX.topRowFeatureCell}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.secondary"
                >
                  {row.feature}
                </Typography>
              </TableCell>
              {products.map((product) => (
                <TableCell
                  key={product.id}
                  align="center"
                  sx={SX.featureValueCell}
                >
                  <PlanCellValue value={row.values[product.id] ?? 'dash'} />
                </TableCell>
              ))}
            </TableRow>
          ))}
          {sections.map((section) => (
            <CollapsibleSection
              key={section.title}
              section={section}
              products={products}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

CollapsedTable.displayName = 'CollapsedTable';
